import { useState, useEffect, useCallback } from 'react';
import { EventSummary } from '@/components/EventSummary';
import { summaryText } from '@/lib/eventSummary';
import { useParams, useNavigate } from 'react-router-dom';
import { devicesApi, eventsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge, StatusIndicator } from '@/components/common/StatusIndicator';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Area, AreaChart
} from 'recharts';
import { 
  AlertTriangle, ArrowLeft, Bell, CheckCircle, XCircle, 
  Cpu, Clock, Activity, Droplets, Wind, Wifi, Radio, Thermometer
} from 'lucide-react';
import { format } from 'date-fns';
import { cn, utcDate } from '@/lib/utils';

function ConnectionBadge({ type }) {
  if (type === 'wifi') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-ibm" data-testid="detail-badge-wifi">
        <Wifi className="h-3 w-3" /> WiFi
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-sky-100 text-sky-700 font-ibm" data-testid="detail-badge-lorawan">
      <Radio className="h-3 w-3" /> LoRaWAN
    </span>
  );
}

function TelemetryChart({ data, dataKeys, colors, title, unit, icon: Icon }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400 font-ibm text-sm">
        Ingen telemetridata tillgänglig
      </div>
    );
  }

  const formatTime = (ts) => {
    if (!ts) return '';
    try { return format(new Date(ts), 'HH:mm'); } catch { return ''; }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
        <h4 className="text-sm font-ibm font-semibold text-slate-700">{title}</h4>
        <span className="text-xs text-slate-400">{unit}</span>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            {dataKeys.map((key, i) => (
              <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[i]} stopOpacity={0.2}/>
                <stop offset="95%" stopColor={colors[i]} stopOpacity={0}/>
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="timestamp" tickFormatter={formatTime} tick={{ fontSize: 10 }} stroke="#94a3b8" />
          <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" width={35} />
          <Tooltip 
            labelFormatter={(v) => { try { return format(new Date(v), 'HH:mm:ss'); } catch { return ''; }}}
            contentStyle={{ fontSize: 12, borderRadius: 4, border: '1px solid #e2e8f0' }}
          />
          {dataKeys.map((key, i) => (
            <Area key={key} type="monotone" dataKey={key} stroke={colors[i]} 
              strokeWidth={1.5} fill={`url(#grad-${key})`} dot={false} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function DeviceDetailPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [telemetry, setTelemetry] = useState([]);
  const [telemetryRange, setTelemetryRange] = useState('60');
  const [loading, setLoading] = useState(true);
  const [testingAlarm, setTestingAlarm] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [deviceRes, statusRes, eventsRes, telemetryRes] = await Promise.all([
        devicesApi.get(deviceId),
        devicesApi.getStatus(deviceId),
        devicesApi.getEvents(deviceId, 20),
        devicesApi.getTelemetry(deviceId, parseInt(telemetryRange)),
      ]);
      setDevice(deviceRes.data);
      setStatus(statusRes.data);
      setEvents(eventsRes.data);
      setTelemetry(telemetryRes.data);
    } catch (error) {
      toast.error('Kunde inte ladda enhetsdata');
      navigate('/devices');
    } finally {
      setLoading(false);
    }
  }, [deviceId, telemetryRange, navigate]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleTestAlarm = async () => {
    setTestingAlarm(true);
    try {
      const response = await devicesApi.testAlarm(deviceId);
      toast.success(`Testlarm skickat! ${response.data.notifications_sent} notifikationer.`);
      loadData();
    } catch (error) {
      toast.error('Kunde inte skicka testlarm');
    } finally {
      setTestingAlarm(false);
    }
  };

  const handleAcknowledge = async (eventId) => {
    try {
      await eventsApi.acknowledge(eventId, { acknowledged_by: 'admin' });
      toast.success('Kvitterad');
      loadData();
    } catch (error) {
      toast.error('Kunde inte kvittera');
    }
  };

  const handleFalsePositive = async (eventId) => {
    try {
      await eventsApi.markFalsePositive(eventId, { false_positive_by: 'admin' });
      toast.success('Markerad som falskt larm');
      loadData();
    } catch (error) {
      toast.error('Kunde inte markera');
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar enhet...</div>
      </div>
    );
  }

  if (!device) return null;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="device-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/devices')} className="mb-2 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Tillbaka
          </Button>
          <div className="flex items-center gap-3">
            <StatusIndicator status={device.status} size="lg" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
                  {device.name || device.device_eui}
                </h1>
                <ConnectionBadge type={device.connection_type} />
                {device.is_placeholder && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-ibm font-semibold" data-testid="detail-badge-attrapp">ATTRAPP</span>
                )}
              </div>
              <p className="text-slate-500 font-ibm">
                {device.school_name} &bull; {device.room_name}
                {device.device_model && <span className="ml-2 text-xs text-slate-400">({device.device_model})</span>}
              </p>
            </div>
          </div>
        </div>
        {device.is_placeholder ? (
          <p className="text-sm text-amber-700 font-ibm max-w-xs" data-testid="attrapp-detail-hint">
            Attrapp – ingen riktig sensor. Larmar aldrig och ger inga offline-varningar.
          </p>
        ) : (
        <Button 
          onClick={handleTestAlarm} disabled={testingAlarm}
          className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm font-ibm shadow-lg"
          data-testid="test-alarm-btn"
        >
          <Bell className="h-4 w-4 mr-2" />
          {testingAlarm ? 'Skickar...' : 'Skicka testlarm'}
        </Button>
        )}
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Status', render: () => device.is_placeholder
            ? <span className="text-sm font-ibm font-semibold text-amber-700" data-testid="detail-status-attrapp">Attrapp</span>
            : <StatusBadge status={device.status} /> },
          { label: 'Senast sedd', render: () => (
            <p className="font-chivo font-bold text-xl tabular-nums">
              {device.last_seen ? format(utcDate(device.last_seen), 'HH:mm:ss') : 'Aldrig'}
            </p>
          )},
          { label: 'Score', render: () => (
            <p className={cn("font-chivo font-black text-2xl tabular-nums",
              device.last_score >= 0.8 && "text-red-600",
              device.last_score >= 0.5 && device.last_score < 0.8 && "text-amber-600",
              device.last_score < 0.5 && "text-emerald-600"
            )}>
              {(device.last_score * 100).toFixed(0)}%
            </p>
          )},
          { label: 'Anslutning', render: () => (
            <p className="font-ibm font-semibold text-lg mt-1">
              {device.connection_type === 'wifi' ? 'WiFi' : 'LoRaWAN'}
            </p>
          )},
        ].map((card) => (
          <Card key={card.label} className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-ibm uppercase tracking-widest text-slate-500">{card.label}</p>
              <div className="mt-2">{card.render()}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Telemetry Charts */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-slate-500" />
              Telemetri
            </CardTitle>
            <Select value={telemetryRange} onValueChange={setTelemetryRange}>
              <SelectTrigger className="w-[140px]" data-testid="telemetry-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 minuter</SelectItem>
                <SelectItem value="30">30 minuter</SelectItem>
                <SelectItem value="60">1 timme</SelectItem>
                <SelectItem value="360">6 timmar</SelectItem>
                <SelectItem value="1440">24 timmar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TelemetryChart 
              data={telemetry} dataKeys={['pm1', 'pm2_5', 'pm10']}
              colors={['#8b5cf6', '#ef4444', '#f97316']}
              title="Partiklar" unit="µg/m³" icon={Wind}
            />
            <TelemetryChart 
              data={telemetry} dataKeys={['voc']}
              colors={['#eab308']}
              title="VOC" unit="ppb" icon={Droplets}
            />
            <TelemetryChart 
              data={telemetry} dataKeys={['temperature']}
              colors={['#06b6d4']}
              title="Temperatur" unit="°C" icon={Thermometer}
            />
            <TelemetryChart 
              data={telemetry} dataKeys={['humidity']}
              colors={['#3b82f6']}
              title="Fuktighet" unit="%" icon={Droplets}
            />
          </div>
        </CardContent>
      </Card>

      {/* Baseline Values */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <Wind className="h-5 w-5 text-slate-500" />
            Baslinje
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
            {[
              { label: 'PM1', value: status?.baseline_pm1, unit: 'µg/m³' },
              { label: 'PM2.5', value: status?.baseline_pm2_5, unit: 'µg/m³' },
              { label: 'PM10', value: status?.baseline_pm10, unit: 'µg/m³' },
              { label: 'VOC', value: status?.baseline_voc, unit: 'ppb' },
              { label: 'Fuktighet', value: status?.baseline_humidity, unit: '%' },
            ].map((item) => (
              <div key={item.label} className="p-3 bg-slate-50 rounded-sm">
                <p className="text-xs font-ibm uppercase tracking-widest text-slate-500">{item.label}</p>
                <p className="font-chivo font-bold text-lg tabular-nums">
                  {item.value?.toFixed(1) || '-'} <span className="text-xs text-slate-400 ml-1">{item.unit}</span>
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3 font-ibm" data-testid="device-last-summary">
            {device.status === 'vape' || device.status === 'suspect'
              ? summaryText({ event_type: device.status, explanation: device.last_explanation, score: device.last_score })
              : device.is_placeholder ? 'Attrapp – tar inte emot någon data.'
              : device.status === 'offline' ? 'Sensorn skickar inte data just nu.' : 'Normal drift – inga avvikelser.'}
          </p>
        </CardContent>
      </Card>

      {/* Pilot Mode Card */}
      {!device.is_placeholder && (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-chivo font-bold text-amber-900">Pilotlage - Testa din installation</h3>
              <p className="text-sm text-amber-700 mt-1">
                Klicka "Skicka testlarm" for att verifiera hela notifikationskedjan. Det kommer:
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                <li>Skapa ett simulerat VAPE-event (score: 95%)</li>
                <li>Skicka Teams-notifikation via webhook</li>
                <li>Skicka e-post till konfigurerade mottagare</li>
              </ul>
              <p className="text-xs text-amber-600 mt-3 font-ibm">
                Enhets-ID: <span className="font-mono">{device.device_eui}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Events */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-slate-500" />
            Senaste händelser
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {events.map((event) => (
              <div key={event.id} className={cn(
                "p-4 rounded-sm border",
                event.severity === 'high' && "border-red-300 bg-red-50",
                event.severity === 'medium' && "border-orange-200 bg-orange-50/60",
                !event.severity && event.event_type === 'vape' && "border-red-200 bg-red-50",
                !event.severity && event.event_type === 'suspect' && "border-amber-200 bg-amber-50",
                event.event_type === 'offline' && "border-slate-200 bg-slate-50",
                event.event_type === 'test_alarm' && "border-purple-200 bg-purple-50"
              )} data-testid={`event-detail-${event.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn("text-xs font-ibm font-bold uppercase tracking-wide",
                        event.event_type === 'vape' && "text-red-700",
                        event.event_type === 'suspect' && "text-amber-700",
                        event.event_type === 'offline' && "text-slate-600",
                        event.event_type === 'test_alarm' && "text-purple-700"
                      )}>
                        {event.event_type === 'vape' ? 'VAPE' : event.event_type === 'suspect' ? 'MISSTÄNKT' : event.event_type === 'test_alarm' ? 'TESTLARM' : event.event_type.toUpperCase()}
                      </span>
                      {event.severity && (
                        <span className={cn(
                          "text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
                          event.severity === 'high' && "bg-red-600 text-white",
                          event.severity === 'medium' && "bg-orange-500 text-white",
                          event.severity === 'low' && "bg-slate-400 text-white"
                        )} data-testid={`severity-${event.id}`}>
                          {event.severity === 'high' ? 'HÖG' : event.severity === 'medium' ? 'MEDEL' : 'LÅG'}
                        </span>
                      )}
                      {event.detection_method === 'ml' && (
                        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200">ML</span>
                      )}
                      <span className="text-xs text-slate-500 tabular-nums">
                        {format(utcDate(event.created_at), 'MMM d, HH:mm:ss')}
                      </span>
                      {event.status === 'resolved' && (
                        <span className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Löst
                        </span>
                      )}
                      {event.is_acknowledged && (
                        <span className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Kvitterad
                        </span>
                      )}
                      {event.is_false_positive && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Falskt larm
                        </span>
                      )}
                    </div>
                    <EventSummary event={event} className="mt-1" />
                  </div>
                  {!event.is_acknowledged && !event.is_false_positive && event.status === 'open' && (
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" variant="outline" onClick={() => handleAcknowledge(event.id)} className="text-xs" data-testid={`acknowledge-btn-${event.id}`}>
                        <CheckCircle className="h-3 w-3 mr-1" /> Kvittera
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleFalsePositive(event.id)} className="text-xs text-slate-500" data-testid={`false-positive-btn-${event.id}`}>
                        <XCircle className="h-3 w-3 mr-1" /> Falskt
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <div className="text-center py-8 text-slate-500 font-ibm">
                Inga händelser registrerade för denna enhet
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
